// @story HIR-HR2
// ProposalReview — one assessment proposal, read and decided: every question
// with the approver's keep / edit / reject (and a reason), the checks the
// Assessment Designer's paper went through, and the step that is next —
// "Save for approval" for the person who asked, "Approve" or "Send back" (with
// a reason) for the school's approver (recruitment:admin). Used in a stage's
// panel on the job's Assessment Pipeline tab (Shohini, 29 Oct 2026); it was
// the body of the Assessment approvals page before.

import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ClipboardCheck, Loader2, CheckCircle2, XCircle, AlertTriangle, ChevronLeft, Pencil, Undo2 } from 'lucide-react';
import api from '../../services/api';
import { Button, Input, StatusPill } from '../../ds';

export const STATUS_WORDS = {
  draft: 'Preview — not saved',
  pending: 'Waiting for approval',
  approved: 'Approved',
  rejected: 'Sent back',
  superseded: 'Replaced by a newer one',
};
const STATUS_TONE = { draft: 'neutral', pending: 'warning', approved: 'success', rejected: 'danger', superseded: 'neutral' };
export const KIND_WORDS = { written_test: 'Written test', ai_interview: 'AI interview questions' };
const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];
const fmt = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

export function Pill({ status }) {
  return <StatusPill tone={STATUS_TONE[status] || 'neutral'}>{STATUS_WORDS[status] || status}</StatusPill>;
}

/* A person's own paper can carry any type the exam page takes. */
const CHOICE = new Set(['mcq', 'single_select', 'true_false', 'multi_select']);
const TYPE_WORDS = {
  mcq: 'Multiple choice', single_select: 'Multiple choice', true_false: 'True or false', multi_select: 'Choose all that apply',
  short_answer: 'Short answer', long_answer: 'Long answer', scenario: 'Scenario', case_study: 'Case study',
};

/* ── one question, with the approver's decision ───────────────────────────── */
function WrittenItem({ q, decision, setDecision, canDecide }) {
  const flagged = q.check?.status === 'needs_teacher_check';
  const [editing, setEditing] = useState(false);
  const d = decision || { decision: 'keep', reason: '' };
  const edited = d.edited || {};
  const val = (k) => (edited[k] !== undefined ? edited[k] : q[k]);
  const setEdit = (patch) => setDecision({ ...d, decision: 'edit', edited: { ...edited, ...patch } });
  const isChoice = CHOICE.has(q.questionType);
  const isKey = (o) => o === val('correctAnswer') || (val('correctAnswers') || []).includes(o);

  return (
    <div className={`rounded-lg border p-4 space-y-2 ${flagged ? 'border-dps-status-yellow bg-dps-status-yellow-soft/40' : 'border-dps-hairline'} ${d.decision === 'reject' ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="text-xs text-dps-ink-3">
          <span className="font-semibold text-dps-ink">{q.itemId}</span> · {TYPE_WORDS[q.questionType] || 'Question'}
          {' '}· {q.points} marks · {q.strand === 'pedagogy' ? 'Teaching' : 'Subject'} · {q.topic} · {q.gradeLevel}
        </div>
        {flagged
          ? <span className="text-[11px] font-semibold text-dps-ink flex items-center gap-1"><AlertTriangle size={13} /> Needs a teacher's check</span>
          : <span className="text-[11px] font-semibold text-dps-green-dark flex items-center gap-1"><CheckCircle2 size={13} /> Checks passed</span>}
      </div>

      {editing && canDecide ? (
        <textarea className="input-dps" rows={3} value={val('question')} onChange={(e) => setEdit({ question: e.target.value })} />
      ) : <p className="text-sm text-dps-ink whitespace-pre-wrap">{val('question')}</p>}

      {isChoice && (
        <ul className="space-y-1">
          {(val('options') || []).map((o, i) => (
            <li key={i} className={`text-sm flex items-center gap-2 ${isKey(o) ? 'font-semibold text-dps-green-dark' : 'text-dps-ink-2'}`}>
              <span className="w-5">{LETTERS[i]}.</span>
              {editing && canDecide && q.questionType !== 'multi_select' ? (
                <>
                  <input className="input-dps py-1" value={o} onChange={(e) => {
                    const opts = [...val('options')]; const was = opts[i]; opts[i] = e.target.value;
                    setEdit({ options: opts, ...(val('correctAnswer') === was ? { correctAnswer: e.target.value } : {}) });
                  }} />
                  <input type="radio" name={`key-${q.itemId}`} checked={o === val('correctAnswer')} onChange={() => setEdit({ correctAnswer: o })} aria-label="Correct answer" />
                </>
              ) : <span>{o}{isKey(o) ? '  ✓ answer key' : ''}</span>}
            </li>
          ))}
        </ul>
      )}
      {!isChoice && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <div className="label-dps mb-1">Model answer</div>
            {editing && canDecide ? <textarea className="input-dps" rows={4} value={val('modelAnswer')} onChange={(e) => setEdit({ modelAnswer: e.target.value })} />
              : <p className="text-dps-ink-2 whitespace-pre-wrap">{val('modelAnswer')}</p>}
          </div>
          <div>
            <div className="label-dps mb-1">Marking rubric</div>
            {editing && canDecide ? <textarea className="input-dps" rows={4} value={val('rubric')} onChange={(e) => setEdit({ rubric: e.target.value })} />
              : <p className="text-dps-ink-2 whitespace-pre-wrap">{val('rubric')}</p>}
          </div>
        </div>
      )}
      {q.explanation && <p className="text-xs text-dps-ink-3">Working: {q.explanation}</p>}
      {(q.check?.reasons || []).length > 0 && (
        <ul className="text-xs text-dps-ink-2 list-disc pl-5">{q.check.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
      )}
      {isChoice && q.check?.solverChoice && (
        <p className="text-xs text-dps-ink-3">Independent re-solve chose {q.check.solverChoice}{q.check.regenerated ? ' (this question was rewritten once after failing a check)' : ''}.</p>
      )}

      {canDecide && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {['keep', 'edit', 'reject'].map((k) => (
            <button key={k} type="button"
              onClick={() => { if (k === 'edit') setEditing(true); else setEditing(false); setDecision({ ...d, decision: k, ...(k !== 'edit' ? { edited: undefined } : {}) }); }}
              className={`text-xs px-3 py-1 rounded-pill border ${d.decision === k ? 'bg-dps-ink text-white border-dps-ink' : 'border-dps-edge text-dps-ink-2'}`}>
              {k === 'keep' ? 'Keep' : k === 'edit' ? <span className="inline-flex items-center gap-1"><Pencil size={12} /> Edit</span> : 'Reject'}
            </button>
          ))}
          {d.decision === 'edit' && (
            <button type="button" className="text-xs text-dps-ink-3 inline-flex items-center gap-1" onClick={() => { setEditing(false); setDecision({ decision: 'keep', reason: '' }); }}>
              <Undo2 size={12} /> Undo edit
            </button>
          )}
          {(d.decision !== 'keep' || flagged) && (
            <input className="input-dps py-1 flex-1 min-w-[220px]" value={d.reason || ''}
              onChange={(e) => setDecision({ ...d, reason: e.target.value })}
              placeholder={d.decision === 'reject' ? 'Why is it rejected?' : d.decision === 'edit' ? 'What did you change, and why?' : 'Checked by a teacher — say what you checked'} />
          )}
        </div>
      )}
    </div>
  );
}

function InterviewItem({ q, decision, setDecision, canDecide }) {
  const d = decision || { decision: 'keep', reason: '' };
  const text = d.edited?.text ?? q.text;
  return (
    <div className={`rounded-lg border border-dps-hairline p-4 space-y-2 ${d.decision === 'reject' ? 'opacity-60' : ''}`}>
      <div className="text-xs text-dps-ink-3"><span className="font-semibold text-dps-ink">{q.itemId}</span> · {q.focusArea}{q.refersToTest ? ' · picks up the written test' : ''}</div>
      {d.decision === 'edit' && canDecide
        ? <textarea className="input-dps" rows={2} value={text} onChange={(e) => setDecision({ ...d, edited: { ...(d.edited || {}), text: e.target.value } })} />
        : <p className="text-sm text-dps-ink">{text}</p>}
      {canDecide && (
        <div className="flex flex-wrap items-center gap-2">
          {['keep', 'edit', 'reject'].map((k) => (
            <button key={k} type="button" onClick={() => setDecision({ ...d, decision: k })}
              className={`text-xs px-3 py-1 rounded-pill border ${d.decision === k ? 'bg-dps-ink text-white border-dps-ink' : 'border-dps-edge text-dps-ink-2'}`}>
              {k === 'keep' ? 'Keep' : k === 'edit' ? 'Edit' : 'Reject'}
            </button>
          ))}
          {d.decision !== 'keep' && (
            <input className="input-dps py-1 flex-1 min-w-[220px]" value={d.reason || ''} onChange={(e) => setDecision({ ...d, reason: e.target.value })}
              placeholder={d.decision === 'reject' ? 'Why is it rejected?' : 'What did you change, and why?'} />
          )}
        </div>
      )}
    </div>
  );
}

/* ── one proposal ─────────────────────────────────────────────────────────── */
export function ProposalView({ proposalId, onBack, onChanged }) {
  const [p, setP] = useState(null);
  const [canApprove, setCanApprove] = useState(false);
  const [decisions, setDecisions] = useState({});
  const [note, setNote] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(() => {
    api.get(`/assessment-proposals/${proposalId}`)
      .then((r) => { setP(r.data.data); setCanApprove(!!r.data.canApprove); })
      .catch((e) => toast.error(e.response?.data?.message || 'Could not open that proposal.'));
  }, [proposalId]);
  useEffect(() => { load(); }, [load]);

  if (!p) return <div className="p-8 text-dps-ink-3 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</div>;

  const items = p.writtenTest?.questions || p.interview?.questions || [];
  const canDecide = canApprove && p.status === 'pending';
  const setDecision = (id) => (d) => setDecisions((all) => ({ ...all, [id]: d }));
  const gate = p.qualityGate || {};
  const cov = p.coverage || {};

  const submit = async () => {
    setBusy('submit');
    try {
      await api.post(`/assessment-proposals/${p.proposalId}/submit`);
      toast.success('Saved for approval. The stage will not reach candidates until it is approved.');
      load(); onChanged();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save it for approval.'); } finally { setBusy(''); }
  };
  const approve = async () => {
    setBusy('approve');
    try {
      const list = Object.entries(decisions).map(([itemId, d]) => ({ itemId, decision: d.decision, reason: d.reason || '', edited: d.edited }));
      await api.post(`/assessment-proposals/${p.proposalId}/approve`, { decisions: list, note });
      toast.success('Approved. It is now on the stage.');
      load(); onChanged();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not approve it.'); } finally { setBusy(''); }
  };
  const reject = async () => {
    setBusy('reject');
    try {
      await api.post(`/assessment-proposals/${p.proposalId}/reject`, { reason: rejectReason });
      toast.success('Sent back. The stage is back to how it was.');
      load(); onChanged();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not send it back.'); } finally { setBusy(''); }
  };

  return (
    <div className="space-y-5">
      {onBack && <button type="button" onClick={onBack} className="btn-dps-ghost text-sm"><ChevronLeft size={16} /> All proposals</button>}
      <div className="card-dps p-6 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="font-heading text-xl font-semibold text-dps-ink">{KIND_WORDS[p.kind]} — {p.stage}</h2>
          <Pill status={p.status} />
        </div>
        <p className="text-sm text-dps-ink-2">
          {p.role?.title} · {p.role?.subject} · {p.role?.gradeBand} · {p.role?.board}. {p.writtenBy === 'person' ? `Written by ${p.draftedBy}.` : `Written by the ${p.draftedBy} for ${p.requestedBy}.`}
          {p.submittedAt ? ` Saved for approval by ${p.submittedBy}, ${fmt(p.submittedAt)}.` : ''}
          {p.decidedAt ? ` ${p.status === 'approved' ? 'Approved' : 'Sent back'} by ${p.decidedBy}, ${fmt(p.decidedAt)}.` : ''}
        </p>
        {p.decisionNote && <p className="text-sm text-dps-ink-2">Note: {p.decisionNote}</p>}
        {p.writtenTest && (
          <p className="text-sm text-dps-ink-2">{items.length} questions · {cov.totalMarks} marks · {p.writtenTest.timeLimitMinutes} minutes · pass mark {p.writtenTest.passMarkPercent}%</p>
        )}
        {p.interview && (
          <p className="text-sm text-dps-ink-2">{items.length} questions · about {p.interview.durationMinutes} minutes · focus areas: {(p.interview.focusAreas || []).join(', ')}</p>
        )}
      </div>

      {p.writtenTest && p.writtenBy !== 'person' && (
        <div className="card-dps p-6 space-y-3">
          <h3 className="font-heading font-semibold text-dps-ink">Checks</h3>
          <ul className="text-sm text-dps-ink-2 space-y-1">
            <li>Multiple choice re-solved independently, without the key: <b>{gate.firstPass?.agreedWithKey ?? 0} of {gate.mcqs}</b> agreed first time.</li>
            <li>Rewritten after failing a check: <b>{gate.regenerated || 0}</b>. Near-duplicates found: <b>{gate.duplicatesFound || 0}</b>.</li>
            <li>Needing a teacher's check now: <b>{(gate.needsTeacherCheck || []).length}</b>{(gate.needsTeacherCheck || []).length ? ` (${gate.needsTeacherCheck.map((x) => x.itemId).join(', ')}) — each must be kept with a note, edited or rejected.` : '.'}</li>
            <li>Subject / teaching split: {cov.split?.content} / {cov.split?.pedagogy} ({cov.split?.contentPercent}% subject; target {cov.split?.target}).</li>
          </ul>
          {cov.table && (
            <div className="overflow-x-auto">
              <table className="text-xs min-w-full">
                <thead><tr className="text-left text-dps-ink-3"><th className="pr-3 py-1">Topic</th>{cov.grades.map((g) => <th key={g} className="px-2">{g}</th>)}<th className="px-2">Total</th></tr></thead>
                <tbody>{cov.table.map((r) => (
                  <tr key={r.topic} className="border-t border-dps-hairline"><td className="pr-3 py-1 text-dps-ink">{r.topic}</td>{cov.grades.map((g) => <td key={g} className="px-2 text-center">{r[g] || ''}</td>)}<td className="px-2 text-center font-semibold">{r.total}</td></tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="space-y-3">
        {items.map((q) => (p.kind === 'written_test'
          ? <WrittenItem key={q.itemId} q={q} decision={decisions[q.itemId]} setDecision={setDecision(q.itemId)} canDecide={canDecide} />
          : <InterviewItem key={q.itemId} q={q} decision={decisions[q.itemId]} setDecision={setDecision(q.itemId)} canDecide={canDecide} />))}
      </div>

      {p.status === 'draft' && (
        <div className="card-dps p-6 flex flex-wrap items-center gap-3">
          <p className="text-sm text-dps-ink-2 flex-1">This is a preview. Saving it sends it to the school's approver; until they approve it, the stage refuses candidates.</p>
          <Button onClick={submit} loading={busy === 'submit'} icon={<ClipboardCheck size={16} />}>Save for approval</Button>
        </div>
      )}
      {p.status === 'pending' && !canApprove && (
        <div className="card-dps p-6 text-sm text-dps-ink-2">Waiting for the school's approver. Approving needs the school administrator's permission.</div>
      )}
      {canDecide && (
        <div className="card-dps p-6 space-y-4">
          <Input id={`approve-note-${p.proposalId}`} label="Note with your approval (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={approve} disabled={!!busy} loading={busy === 'approve'} icon={<CheckCircle2 size={16} />}>Approve</Button>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <Input id={`send-back-${p.proposalId}`} label="Or send it back — say why" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="e.g. Add two questions on integration" />
            </div>
            <Button variant="secondary" onClick={reject} disabled={!!busy || !rejectReason.trim()} loading={busy === 'reject'} icon={<XCircle size={16} />}>Send back</Button>
          </div>
        </div>
      )}
    </div>
  );
}

